/**
 * A staged (not-yet-committed) upload open for preview inside the upload modal.
 *
 * The old staged-evidence screen previewed files by asking the server for a PDF
 * rendition of each one. The new staging flow keeps the original browser `File`
 * on every `StagedFile` (see `useStagedUpload`), and the LegalWork port gave the
 * app a native reader for each supported type, so this routes the local bytes
 * straight to those readers — no upload round-trip and no PDF conversion. It is
 * the read-only twin of `FilePane`, which does the same for committed nodes.
 */
import { BrandLoader } from "@/components/brand";
import { lazy, Suspense, useEffect, useRef, useState, type FC } from "react";

import { filePreviewKind } from "../../caseFiles/filePreview";
import { ImagePreview } from "../editor/ImagePreview";
import type { OfficeEditorApi } from "../../officeEditor/useOfficeEditor";

// Each viewer is a large vendor bundle (PDFium, Univer, the slides and docx
// engines); only people who open the matching file type pay for it, and keeping
// them out of the static graph keeps this module cheap to import (mirrors
// `FilePane`).
const PdfViewer = lazy(() =>
  import("../pdfViewer/PdfViewer").then((m) => ({
    default: m.PdfViewer
  }))
);
const XlsxEditor = lazy(() =>
  import("../../officeEditor/XlsxEditor").then((m) => ({
    default: m.XlsxEditor
  }))
);
const PptxEditor = lazy(() =>
  import("../../officeEditor/PptxEditor").then((m) => ({
    default: m.PptxEditor
  }))
);
const DraftDocxEditor = lazy(() =>
  import("../../draftEditor/DraftDocxEditor").then((m) => ({
    default: m.DraftDocxEditor
  }))
);

type Props = {
  /** The bytes to preview — a staged browser `File` or a fetched node `Blob`. */
  file: Blob;
  name: string;
  /** Stable id used to key the docx recovery/editor per previewed file. */
  id: string;
};

const Loading: FC<{ label: string }> = ({ label }) => (
  <div className="flex h-full items-center justify-center">
    <BrandLoader label={label} />
  </div>
);

const Notice: FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="flex h-full items-center justify-center px-6 text-center text-sm text-muted-foreground">
    {children}
  </div>
);

/** Reads the file once and hands the office editor an ArrayBuffer. */
const OfficeBytes: FC<{
  file: Blob;
  children: (bytes: ArrayBuffer) => React.ReactNode;
}> = ({ file, children }) => {
  const [bytes, setBytes] = useState<ArrayBuffer | null>(null);
  useEffect(() => {
    let cancelled = false;
    setBytes(null);
    file.arrayBuffer().then((buffer) => !cancelled && setBytes(buffer));
    return () => {
      cancelled = true;
    };
  }, [file]);
  if (!bytes) return <Loading label="Loading file" />;
  return <>{children(bytes)}</>;
};

const noop = () => {};
const noopSave = async () => {};

/**
 * Routes a staged file to the reader that matches it (`filePreviewKind`, the
 * same matrix `FilePane` uses):
 *
 * - PDF: the PDF viewer on the local bytes.
 * - Image: the image itself.
 * - Word / sheet / slides (OOXML): the in-app editor, read-only, on the local
 *   bytes — the CaseBlink editor shells are tied to a committed node, so this
 *   uses the vendor viewers underneath them directly.
 * - Legacy Office / anything else: no in-app preview before it is added (the
 *   old flow leaned on a server PDF conversion that only exists post-commit).
 */
export const StagedFilePreview: FC<Props> = ({ file, name, id }) => {
  const kind = filePreviewKind({ name, kind: "file" });
  const apiRef = useRef<OfficeEditorApi | null>(null);

  if (kind === "pdf") {
    return (
      <Suspense fallback={<Loading label="Loading the PDF viewer" />}>
        <PdfViewer file={file} ariaLabel={name} downloadName={name} />
      </Suspense>
    );
  }

  if (kind === "image") {
    return <ImagePreview file={file} name={name} />;
  }

  if (kind === "word") {
    return (
      <div className="h-full min-h-0" data-testid="staged-preview-word">
        <Suspense fallback={<Loading label="Loading the Word viewer" />}>
          <DraftDocxEditor
            key={id}
            draftId={`staged:${id}`}
            recoveryKey={`staged:${id}`}
            readOnly
            title={name}
            loadDocx={async () => ({
              bytes: await file.arrayBuffer(),
              filename: name,
              unresolvedCitations: 0,
              updatedAt: null
            })}
          />
        </Suspense>
      </div>
    );
  }

  if (kind === "sheet" || kind === "slides") {
    const label =
      kind === "sheet"
        ? "Loading the spreadsheet viewer"
        : "Loading the slides viewer";
    return (
      <div className="h-full min-h-0" data-testid={`staged-preview-${kind}`}>
        <Suspense fallback={<Loading label={label} />}>
          <OfficeBytes file={file}>
            {(bytes) =>
              kind === "sheet" ? (
                <XlsxEditor
                  name={name}
                  content={bytes}
                  readOnly
                  onSave={noopSave}
                  onDirtyChange={noop}
                  apiRef={apiRef}
                />
              ) : (
                <PptxEditor
                  name={name}
                  content={bytes}
                  readOnly
                  onSave={noopSave}
                  onDirtyChange={noop}
                  apiRef={apiRef}
                />
              )
            }
          </OfficeBytes>
        </Suspense>
      </div>
    );
  }

  return (
    <Notice>
      This file type can&rsquo;t be previewed before it&rsquo;s added to the
      case.
    </Notice>
  );
};
